import categoryService from "app/common/services/categoryService";
import { chosenItems } from "app/common/services/chosenItems";
import { chosenSites } from "app/common/services/chosenSites";
import { swSettings } from "app/common/services/swSettings";
import {
    CAMPAIGNS_ALL_TIME_ID,
    CAMPAIGNS_DURATION_ONE_WEEK_ID,
} from "app/components/campaigns/queryBar-report/reports-pages-filters/durationPicker/CampaignDurationPicker.consts";
import { EFamilyTypes } from "app/components/dashboard/dashboard-templates/components/DashboardTemplatesTypes";
import widgetSettings from "app/components/dashboard/WidgetSettings";
import type { CampaignConfigForDashboard } from "app/pages/digital-marketing/keywords-campaigns/reports/keywords-report/keywords-table/KeywordsReportTypes";
import type { NavigatorType } from "single-spa/react-app/ProNavigator";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { BasicDurations } from "app/services/DurationService";

import type {
    IWidgetWebsiteKey,
    IWidgetMobileKey,
    IWidgetModelTypesType,
    IWidgetModelTypesWebSource,
    IWidgetModel,
    IWidgetModelFilters,
} from "./widget-types/Widget";
import { keywordGroupsQueryClient } from "app/services/keyword-groups";

export const getWebsiteDuration = (durationParam, currentComponent, showLastOneMonth?) => {
    let lastOneMonthDurationValue;
    if (showLastOneMonth) {
        lastOneMonthDurationValue = currentComponent?.datePickerPresets?.[1]?.value;
    } else {
        lastOneMonthDurationValue = currentComponent?.datePickerPresets?.[0]?.value;
    }
    const isFixedSnapshotRangeLengthOfOne = currentComponent?.resources?.FixedSnapshotRangeLength === 1;
    return isFixedSnapshotRangeLengthOfOne && durationParam === lastOneMonthDurationValue
        ? BasicDurations.LAST_MONTH
        : durationParam;
};

/**
 * Until all the PRO will use widgets, this service will be here to help normalize IWidgetModel
 */

export class WidgetModelAdapterService {
    public static _instance: WidgetModelAdapterService;

    private _swNavigator?: NavigatorType;

    private _chosenSites?: any;

    public static getInstance() {
        if (!WidgetModelAdapterService._instance) {
            WidgetModelAdapterService._instance = new WidgetModelAdapterService();
        }
        return WidgetModelAdapterService._instance;
    }

    constructor() {
        this._swNavigator = proNavigator;
        this._chosenSites = chosenSites;
    }

    private _getPageFiltersByMetric = {
        OutgoingReferrals: () => {
            const pageFilters = this._swNavigator.getParams().outagoing_filters;
            if (pageFilters) {
                let widgetFilters = ``;
                pageFilters.split("+").forEach((filter, i) => {
                    if (i > 0) {
                        widgetFilters += `,`;
                    }
                    if (filter.split(";")[0] == "domain") {
                        widgetFilters += `domain;contains;"${filter.split(";")[2]}"`;
                    }
                    if (filter.split(";")[0] == "category") {
                        widgetFilters += `category;category;"${filter.split(";")[2]}"`;
                    }
                });
                return widgetFilters;
            }
            return {};
        },
    };

    /**
     * Creates key from 'chosenSites' service
     *
     * @returns Array<IWidgetWebsiteKey>
     */
    private _getKeysFromSiteInfo(): Array<IWidgetWebsiteKey> {
        return this._chosenSites.map(function (site, siteInfo) {
            return {
                name: siteInfo.isVirtual ? site.replace("*", "") : site, //when virtual name added one more * added
                isVirtual: siteInfo.isVirtual,
                /**
                 * P: save space in DB and reduce widget footprint
                 * S: remove image from the key and make widget in dashboard dynamically create image every time
                 * P: each widget creates ajax request in order to get an image(hash generated on back end)
                 * S: leave as it is now. Save image path in DB
                 * todo: Danny has solution commented on backend. Make new branch and integrate with him, then remove two lines below
                 */
                image: siteInfo.icon,
            };
        });
    }

    /*** Create key from 'selectedSite' query param.
     *
     * @returns Array<IWidgetWebsiteKey>
     */
    private _getKeyFromPrimarySite(): Array<any> {
        const _selectedSite = this._swNavigator.getParams().selectedSite;
        let _primarySite;
        if (_selectedSite) {
            _primarySite = this._chosenSites.getInfo(_selectedSite);
        } else {
            _primarySite = this._chosenSites.getPrimarySite();
        }
        return [
            {
                name: _primarySite.name,
                isVirtual: _primarySite.isVirtual,
                image: _primarySite.icon,
            },
        ];
    }

    /*** Create key from keyword
     *
     * @returns Array<IWidgetKeywordsKey>
     */
    private async _getKeyFromKeyword(): Promise<Array<any>> {
        let _keyword = this._swNavigator.getParams().keyword;
        let name = _keyword;
        if (_keyword.startsWith("*")) {
            _keyword = _keyword.substring(1);
            const group = await keywordGroupsQueryClient.getGroupById(_keyword);
            name = group?.Name ?? _keyword;
        }
        return [
            {
                name: name,
                id: _keyword,
            },
        ];
    }

    /**
     * Creates key from 'choosenItems' service
     *
     * @returns Array<IWidgetMobileKey>
     */
    private _getKeysFromChosenItems(): Array<IWidgetMobileKey> {
        return chosenItems.$all().map(function (item) {
            return {
                name: item.Title,
                id: item.Id,
                store: item.AppStore.toLowerCase(),
                appkey: (item.AppStore === "Google" ? 0 : 1) + "_" + item.Id,
            };
        });
    }

    /**
     * Receives metric string and returns proper one from the SimilarWebPro.Website/app/components/widget/widget-config/metrics/*
     *
     * @param metric
     * @returns {string}
     */
    public normalizeMetric(metric): string {
        switch (metric) {
            case "AppEngagementCategoryRank":
                return "AppIndexes";
            default:
                return metric;
        }
    }

    /**
     * Creates IWidgetModel from the params, creates keys from the _getKeysFromChosenItems
     *
     * @param metric
     * @param type
     * @param webSource
     * @returns IWidgetModel
     */
    public fromMobile(
        metric: string,
        type: IWidgetModelTypesType,
        webSource: IWidgetModelTypesWebSource,
        width?,
        filters?,
    ): IWidgetModel {
        const { country, duration, device, comparedDuration } = this._swNavigator.getParams();

        return {
            country,
            duration,
            comparedDuration,
            device,
            family: widgetSettings.getMetricProperties(metric).family,
            type: type,
            webSource: webSource,
            metric: metric,
            key: this._getKeysFromChosenItems(),
            width,
            filters,
        };
    }

    /**
     * Checks if the current view shows main domain only or include sub-domains.
     * @returns {object}
     */
    public getIncludeSubDomains() {
        const includeSubDomains = this._swNavigator.getParams().isWWW === "*";
        return { includeSubDomains };
    }

    public getUrlFilters(metric) {
        if (this._getPageFiltersByMetric[metric]) {
            return { filter: this._getPageFiltersByMetric[metric]() };
        }
        return {};
    }

    public fromIndustry(
        metric: string,
        type: IWidgetModelTypesType,
        webSource: IWidgetModelTypesWebSource,
        industryQueryParams: string,
        filters?: any,
    ): IWidgetModel {
        const selectedIndustry = categoryService.categoryQueryParamToCategoryObject(industryQueryParams);

        const modelFilters = {
            ...widgetSettings.getMetricWidgetFilters(metric, type, false, true),
            ...this.getIncludeSubDomains(),
            ...this.getUrlFilters(metric),
            ...filters,
        };

        return {
            country: this._swNavigator.getParams().country,
            duration: this._swNavigator.getParams().duration,
            comparedDuration: this._swNavigator.getParams().comparedDuration,
            family: "Industry",
            filters: modelFilters,
            webSource: webSource,
            type: type,
            metric: metric,
            key: [
                {
                    id: selectedIndustry.forApi,
                    name: selectedIndustry.name,
                    type: "Industry",
                },
            ],
        };
    }

    /**
     * Creates IWidgetModel from the params, creates keys from the _getKeysFromSiteInfo
     * @returns IWidgetModel
     */
    public fromWebsite(
        metric: string,
        type: IWidgetModelTypesType,
        webSource: IWidgetModelTypesWebSource,
        selectedSite?: boolean,
        filters?,
        duration?: string,
    ): IWidgetModel {
        const _key = selectedSite ? this._getKeyFromPrimarySite() : this._getKeysFromSiteInfo();
        const _isCompare = _key.length > 1;
        const _filters = Object.assign(
            widgetSettings.getMetricWidgetFilters(metric, type, _isCompare, true),
            this.getIncludeSubDomains(),
            this.getUrlFilters(metric),
            filters,
        );
        return {
            country: this._swNavigator.getParams().country,
            duration:
                duration && !widgetSettings.getMetricProperties(metric).showLastOneMonth
                    ? duration
                    : getWebsiteDuration(
                          this._swNavigator.getParams().duration,
                          swSettings.current,
                          widgetSettings.getMetricProperties(metric).showLastOneMonth,
                      ),
            comparedDuration: this._swNavigator.getParams().comparedDuration,
            family: widgetSettings.getMetricProperties(metric).family,
            type: type,
            webSource: webSource,
            metric: metric,
            key: _key,
            filters: _filters,
        };
    }

    public fromRankTracker(
        metric: string,
        type: IWidgetModelTypesType,
        keys,
        width: 1 | 2 | 3 | 4,
        webSource: IWidgetModelTypesWebSource,
        currentCampaignConfiguration: CampaignConfigForDashboard,
        country: string,
        duration: string,
        filters?,
    ): IWidgetModel {
        let durationFromParams = duration;
        const isLastWeekSelected = CAMPAIGNS_DURATION_ONE_WEEK_ID === duration;
        if (!isLastWeekSelected && duration?.includes("d")) {
            durationFromParams = CAMPAIGNS_ALL_TIME_ID;
        }
        return {
            duration: durationFromParams,
            type: type,
            metric: metric,
            family: EFamilyTypes.RankTracker,
            key: keys,
            width,
            webSource,
            comparedDuration: true,
            currentCampaignConfiguration,
            filters,
            country,
        };
    }

    public fromSegment(
        metric: string,
        type: IWidgetModelTypesType,
        webSource: IWidgetModelTypesWebSource,
        filters: IWidgetModelFilters,
        selectedSite?: boolean,
    ): IWidgetModel {
        const key = selectedSite ? this._getKeyFromPrimarySite() : this._getKeysFromSiteInfo();
        const isCompare = key.length > 1;
        const generatedFilters = {
            ...widgetSettings.getMetricWidgetFilters(metric, type, isCompare, true),
            ...this.getIncludeSubDomains(),
            ...this.getUrlFilters(metric),
            ...filters,
        };
        const { country, comparedDuration, duration } = this._swNavigator.getParams();

        return {
            country,
            duration: getWebsiteDuration(duration, swSettings.current),
            comparedDuration,
            family: widgetSettings.getMetricProperties(metric).family,
            type: type,
            webSource: webSource,
            metric: metric,
            key: key,
            filters: generatedFilters,
        };
    }

    /**
     * Creates IWidgetModel for keyword from the params
     *
     * @param metric
     * @param type
     * @param webSource
     * @returns IWidgetModel
     */
    public async fromKeyword(
        metric: string,
        type: IWidgetModelTypesType,
        webSource: IWidgetModelTypesWebSource,
        filters?,
        key?,
        family?,
        customAsset?,
    ): Promise<IWidgetModel> {
        const _family = family ? family : widgetSettings.getMetricProperties(metric).family;
        const _key = key ? [key] : await this._getKeyFromKeyword();
        const _filters = Object.assign(
            widgetSettings.getMetricWidgetFilters(metric, type, false, true),
            this.getIncludeSubDomains(),
            this.getUrlFilters(metric),
            filters,
        );
        return {
            country: this._swNavigator.getParams().country,
            duration: widgetSettings.getMetricProperties(metric).showLastOneMonth
                ? getWebsiteDuration(
                      this._swNavigator.getParams().duration,
                      swSettings.current,
                      widgetSettings.getMetricProperties(metric).showLastOneMonth,
                  )
                : this._swNavigator.getParams().duration,
            family: _family,
            type: type,
            webSource: webSource,
            metric: metric,
            key: _key,
            filters: _filters,
            customAsset: customAsset,
        };
    }

    public fromAdIntelligence(
        metric: string,
        type: IWidgetModelTypesType,
        key,
        webSource: IWidgetModelTypesWebSource,
        country: string,
        duration: string,
        customAsset: string,
        filters?,
    ): IWidgetModel {
        return {
            duration: this._swNavigator.getParams().duration,
            type: type,
            metric: metric,
            family: EFamilyTypes[customAsset],
            key: this._getKeysFromSiteInfo(),
            webSource: this._swNavigator.getParams().webSource,
            filters,
            country: this._swNavigator.getParams().country,
        };
    }

    /**
     * Creates IWidgetModel from the params, creates keys from the _getKeysFromChosenItems
     *
     * @param metric
     * @param type
     * @param webSource
     * @param filters
     * @param key
     * @param family
     * @param customAsset
     * @returns IWidgetModel
     */
    public noModelType(
        metric: string,
        type: IWidgetModelTypesType,
        webSource: IWidgetModelTypesWebSource,
        filters?,
        key?,
        family?,
        customAsset?,
    ): IWidgetModel {
        const _family = family ? family : widgetSettings.getMetricProperties(metric).family;
        const _key = key;

        const _filters = Object.assign(
            widgetSettings.getMetricWidgetFilters(metric, type, false, true),
            this.getIncludeSubDomains(),
            this.getUrlFilters(metric),
            filters,
        );
        return {
            country: this._swNavigator.getParams().country,
            duration: this._swNavigator.getParams().duration,
            family: _family,
            type: type,
            webSource: webSource,
            metric: metric,
            key: _key,
            filters: _filters,
            customAsset: customAsset,
        };
    }
}
